import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { Drawer, Table, Typography, Tooltip, Empty, Grid } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import GET_CAMPAING_ORDER_BASIC_DETAILS_CONNECTION from '../../../../../core/GraphQl/Queries/GET_CAMPAING_ORDER_BASIC_DETAILS_CONNECTION';
import StatusTag, { getServedImpressions } from '../../../Campaigns/shared/StatusTag';
import TagsPagination from './TagsPagination';
import AppSpinner from '../../../Tasks/components/shared/AppSpinner';
import { dateFormat, drawer } from '../../../Tasks/configs/designTokens';
import '../../styles/index.scss';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const DRAWER_WIDTH_DESKTOP = drawer.lg;
const DRAWER_WIDTH_MOBILE = drawer.mobile;

const CampaignsDrawer = ({ visible, onClose, tagId, tagName }) => {
  const screens = useBreakpoint();
  const drawerWidth = !screens.md ? DRAWER_WIDTH_MOBILE : DRAWER_WIDTH_DESKTOP;

  const { data, loading, error } = useQuery(GET_CAMPAING_ORDER_BASIC_DETAILS_CONNECTION, {
    variables: {
      where: {
        transactionTarget: tagId,
      },
      first: 100,
      orderBy: 'startDate_DESC',
    },
    skip: !tagId || !visible,
    fetchPolicy: 'cache-and-network',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const campaigns =
    data?.campaignOrdersConnection?.edges?.map((edge) => ({
      ...edge.node,
      key: edge.node.id,
    })) || [];

  const paginatedCampaigns = campaigns.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '45%',
      render: (text, record) => (
        <div className="tags-campaigns-drawer__name-cell">
          <Tooltip title={text} placement="topLeft">
            <Link
              to={`/campaigns/details/${record.id}/1`}
              onClick={(e) => e.stopPropagation()}
              className="tags-campaigns-drawer__campaign-name"
            >
              {text}
            </Link>
          </Tooltip>
          {record.advertiser && (
            <span className="tags-campaigns-drawer__advertiser-label">
              {record.advertiser.name}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      render: (status, record) => (
        <div className="tags-campaigns-drawer__status-cell">
          <StatusTag
            startDate={record.startDate}
            endDate={record.endDate}
            attributionWindow={record.attributionWindow || 14}
            statusCampaign={status}
            impressions={getServedImpressions(record)}
          />
        </div>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: '17.5%',
      render: (date) => (
        <Text className="tags-campaigns-drawer__date">
          {date ? moment(date).format(dateFormat.dateOnly) : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      width: '17.5%',
      render: (date) => (
        <Text className="tags-campaigns-drawer__date">
          {date ? moment(date).format(dateFormat.dateOnly) : 'N/A'}
        </Text>
      ),
    },
  ];

  return (
    <Drawer
      className="tags-campaigns-drawer"
      title={
        <div>
          <Text strong className="tags-campaigns-drawer__title">Campaigns using tag: </Text>
          <Text type="secondary" className="tags-campaigns-drawer__subtitle">{tagName}</Text>
        </div>
      }
      placement="right"
      width={drawerWidth}
      onClose={onClose}
      visible={visible}
    >
      {loading ? (
        <div className="tags-campaigns-drawer__loading">
          <AppSpinner size="large" />
        </div>
      ) : error ? (
        <Empty description="Error loading campaigns" />
      ) : campaigns.length === 0 ? (
        <Empty description="No campaigns found using this tag" />
      ) : (
        <>
          <Text type="secondary" className="tags-campaigns-drawer__count">
            Found {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
          </Text>
          <div className="tags-campaigns-drawer__table">
            <Table
              aria-label="Campaigns using this tag"
              dataSource={paginatedCampaigns}
              columns={columns}
              pagination={false}
              size="middle"
              scroll={{ x: true }}
              rowClassName={(_record, index) =>
                index % 2 === 0 ? 'tags-campaigns-drawer__row--even' : 'tags-campaigns-drawer__row--odd'
              }
            />
          </div>
          <TagsPagination
            current={currentPage}
            pageSize={pageSize}
            total={campaigns.length}
            onChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            compact
          />
        </>
      )}
    </Drawer>
  );
};

export default CampaignsDrawer;
